import { useAuth } from '@teko/auth-react';
import {
  Body,
  BottomActionBar,
  BottomSheet,
  Button,
  Modal,
  ModalBody,
  Shape,
  useModal,
} from '@teko/ds-mobile';
import { logger } from '@teko/log';
import { PrivacyPolicy, SubmittedPrivacyPolicy } from '@teko/privacy-policy';
import { trackManually } from '@teko/tracking';
import { parseJsonStringSafely } from '@teko/utils';
import { PrivacyPolicies } from 'components/PrivacyPolicies';
import { useFeatureFlags, useGlobalData } from 'contexts';
import { isBankPartner, isServerless, onSignOut } from 'helpers';
import { t } from 'i18n';
import { useRouter } from 'next/router';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { PolicyTypeId } from 'types';
import {
  LOCAL_STORAGE_ALLOWED_PRIVACY_POLICY,
  LOCAL_STORAGE_PRIVACY_POLICIES,
  LOCAL_STORAGE_SUBMIT_PRIVACY_POLICIES,
  LOCAL_STORAGE_UNLOGGED_USER_PRIVACY_POLICIES,
  LOGIN_METHOD,
} from '../constants';
import { useTenantConfig } from './GlobalDataContext';
import { useServices } from './ServiceContext';

export interface PrivacyPolicyContext {
  loading: boolean;
  policies: PrivacyPolicy[];
  shouldShowSelector: boolean;
  showSelector: (callback: Function, isMobile?: boolean) => void;
  submitPoliciesApproval: (callback?: Function) => Promise<void>;
  buildPrivacyPolicyList: () => ReactNode;
  isByPassShowPoliciesForUnLoggerUser?: (
    loginMethod: string,
    userPhone?: string,
  ) => boolean;
}

interface LocalStorageSubmitPolicies {
  policies: SubmittedPrivacyPolicy[];
  isSubmitted: boolean;
  createdAt: string;
}

interface UserResponsePolicy {
  id: number;
  isApprovalRequired: boolean;
  isApproved?: boolean;
}

interface LocalStoragePolicies {
  policies?: PrivacyPolicy[];
  createdAt: string;
  userResponse?: {
    facebookMethod?: UserResponsePolicy[];
    googleMethod?: UserResponsePolicy[];
    phoneMethod?: {
      [key: string]: UserResponsePolicy[];
    };
  };
}

interface LocalStorageUnloggedUserPrivacyPolicies {
  policies?: PrivacyPolicy[];
  facebookUserSelectedPolicies?: UserResponsePolicy[];
  googleUserSelectedPolicies?: UserResponsePolicy[];
  phoneUserSelectedPolicies?: {
    [key: string]: UserResponsePolicy[];
  };
}

interface LocalStorageAllowedStatus {
  isAllowed: boolean;
  createdAt: string;
}

const initialValues = {
  loading: false,
  policies: [],
  shouldShowSelector: false,
  showSelector: () => {},
  submitPoliciesApproval: async () => {},
  buildPrivacyPolicyList: () => <></>,
};

const PrivacyPolicyContext = createContext<PrivacyPolicyContext>(initialValues);

// currently cached submit payload is hardcoded to expire after 3 days.
const submitPayloadTTL = 3;

export const PrivacyPolicyProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const router = useRouter();
  const { user, loading: authLoading, authService } = useAuth();
  const { privacyPolicyService, notificationService } = useServices();
  const { showModal } = useModal();
  const { flags } = useFeatureFlags();
  const {
    privacyPolicyCacheTTL = 30,
    isMobile,
    currentLanguage,
    notificationAppId,
    epicOmni1825,
  } = useTenantConfig();
  const { partner } = useGlobalData();

  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [policies, setPolicies] = useState<PrivacyPolicy[]>([]);
  const [shouldShowSelector, setShouldShowSelector] = useState(false);
  const submitCallback = useRef<Function>(() => {});
  const [userSelectedLoginMethod, setUserSelectedLoginMethod] = useState<{
    loginMethod: string;
    userPhone?: string;
  }>();

  const isLoginPage = !!router.pathname.endsWith('/login');

  const saveUserPoliciesSelectedToLocalStorage = (
    policies: PrivacyPolicy[],
  ) => {
    const LSKey = `${LOCAL_STORAGE_UNLOGGED_USER_PRIVACY_POLICIES}_${
      currentLanguage || 'vi'
    }`;
    const privacyPolicies: LocalStorageUnloggedUserPrivacyPolicies | undefined =
      parseJsonStringSafely(localStorage.getItem(LSKey));
    const savingPolicies: UserResponsePolicy[] = policies.map((policy) => ({
      id: policy.id,
      isApprovalRequired: policy.isApprovalRequired,
      isApproved: policy.isApproved,
    }));

    if (!!userSelectedLoginMethod && !!savingPolicies) {
      const { loginMethod, userPhone } = userSelectedLoginMethod;
      const newPrivacyPolicies: LocalStorageUnloggedUserPrivacyPolicies = {
        ...(privacyPolicies || {}),
        ...(loginMethod === LOGIN_METHOD.FACEBOOK && {
          facebookUserSelectedPolicies: savingPolicies,
        }),
        ...(loginMethod === LOGIN_METHOD.GOOGLE && {
          googleUserSelectedPolicies: savingPolicies,
        }),
        ...(loginMethod === LOGIN_METHOD.PHONE &&
          !!userPhone && {
            phoneUserSelectedPolicies: {
              ...(privacyPolicies?.phoneUserSelectedPolicies || {}),
              [userPhone]: savingPolicies,
            },
          }),
      };
      localStorage.setItem(LSKey, JSON.stringify(newPrivacyPolicies));
    }
  };

  const submitPoliciesApproval = async (callback?: Function) => {
    if (validateApproval(policies)) {
      try {
        setLoading(true);
        await internalSubmit(authService.isLoggedIn());
        if (!!userSelectedLoginMethod) {
          // saving user policies selected to localStorage
          saveUserPoliciesSelectedToLocalStorage(policies);
        }
      } catch (e) {
        // submit failed, cache saved for retry later so no need to do anything
      } finally {
        setShow(false);
        setLoading(false);
        callback?.();
      }
    } else {
      showWarningModal();
    }
  };

  /**
   * Underlying logic to handle fetch policies from api and save to cache
   * */
  const internalGetPolicies = useCallback(
    async (isAuthed?: boolean) => {
      const policies = await privacyPolicyService.getPolicies(undefined, [
        PolicyTypeId.TYPE_1,
        PolicyTypeId.TYPE_2,
        PolicyTypeId.TYPE_3,
      ]);
      const isUserAllowed = checkUserAllowed(policies);
      const now = new Date().toString();

      setPolicies(policies);
      setShouldShowSelector(policies.length > 0);

      // Dont cache user specified policies
      if (!isAuthed) {
        localStorage.setItem(
          `${LOCAL_STORAGE_PRIVACY_POLICIES}_${currentLanguage || 'vi'}`,
          JSON.stringify({
            policies,
            createdAt: now,
          } as LocalStoragePolicies),
        );
      } else {
        localStorage.setItem(
          LOCAL_STORAGE_ALLOWED_PRIVACY_POLICY,
          JSON.stringify({
            isAllowed: isUserAllowed,
            createdAt: now,
          } as LocalStorageAllowedStatus),
        );
      }
    },
    [currentLanguage, privacyPolicyService],
  );

  /**
   * Underlying logic to submit approval to remote api and handle error
   * */
  const internalSubmit = useCallback(
    async (
      isAuthed: boolean,
      customPolicies?: SubmittedPrivacyPolicy[],
      skipSave = false,
    ) => {
      // policy type 1 is only used to show to user
      const submitPolicies: SubmittedPrivacyPolicy[] =
        customPolicies ||
        policies
          .filter((policy) => policy.policyTypeId !== PolicyTypeId.TYPE_1)
          .map(
            ({ id, version, language, isApproved }) =>
              ({
                id,
                version,
                language,
                isApproved: isApproved || false,
              } as SubmittedPrivacyPolicy),
          );
      try {
        // only submit when accessToken is available
        if (isAuthed) {
          await privacyPolicyService.submitPolicies(submitPolicies);
          localStorage.removeItem(LOCAL_STORAGE_SUBMIT_PRIVACY_POLICIES);
        } else !skipSave && saveSubmitPolicies(submitPolicies);
      } catch (e) {
        logger.error(e);
        // saved for retry later
        if (!skipSave) saveSubmitPolicies(submitPolicies);
        throw e;
      }
    },
    [policies, privacyPolicyService],
  );

  /**
   * Submit if payload cached
   * @param {boolean} isAuthed - authenticate status
   * @param {LocalStorageSubmitPolicies} [savedSubmitPolicies] - saved submit payload in cached
   * */
  const validateSubmitPolicies = useCallback(
    async (
      isAuthed: boolean,
      savedSubmitPolicies?: LocalStorageSubmitPolicies,
    ) => {
      if (isAuthed && savedSubmitPolicies) {
        const { policies, isSubmitted } = savedSubmitPolicies;
        if (!!policies && isSubmitted === false) {
          await internalSubmit(isAuthed, savedSubmitPolicies.policies, true);
        }
      }
    },
    [internalSubmit],
  );

  /**
   * Decide to using cache or fetch from api depend on authenticate status and saved values
   * @param {boolean} isAuthed - authenticate status
   * @param {LocalStoragePolicies} [savedGetPolicies] - saved GetPolicies response.
   * @param {LocalStorageAllowedStatus} [savedAllowedStatus] - saved user allow status.
   * */
  const validatePolicies = useCallback(
    async (
      isAuthed: boolean,
      savedAllowedStatus?: LocalStorageAllowedStatus,
    ) => {
      try {
        if (!isAuthed) {
          const savedGetPoliciesKey = `${LOCAL_STORAGE_PRIVACY_POLICIES}_${
            currentLanguage || 'vi'
          }`;
          const savedGetPolicies: LocalStoragePolicies | undefined =
            parseJsonStringSafely(localStorage.getItem(savedGetPoliciesKey));

          if (
            !savedGetPolicies ||
            checkCacheExpired(
              savedGetPolicies?.createdAt,
              privacyPolicyCacheTTL,
            )
          ) {
            await internalGetPolicies();
            const newSavedGetPolicies: LocalStoragePolicies | undefined =
              parseJsonStringSafely(localStorage.getItem(savedGetPoliciesKey));
            if (
              !!savedGetPolicies &&
              !!newSavedGetPolicies &&
              JSON.stringify(savedGetPolicies.policies) !==
                JSON.stringify(newSavedGetPolicies.policies)
            ) {
              localStorage.removeItem(
                `${LOCAL_STORAGE_UNLOGGED_USER_PRIVACY_POLICIES}_${
                  currentLanguage || 'vi'
                }`,
              );
            }
          } else {
            const savedPolicies = savedGetPolicies.policies || [];
            setPolicies(savedPolicies);
            setShouldShowSelector(savedPolicies.length > 0);
          }
        } else if (
          !savedAllowedStatus ||
          (savedAllowedStatus && !savedAllowedStatus.isAllowed)
        ) {
          // fetch privacy policies specify to this user then skip cache step
          internalGetPolicies(true);
        }
      } catch (e) {
        // do nothing for now
      }
    },
    [internalGetPolicies],
  );

  const showSelector = useCallback(async (callback: Function) => {
    submitCallback.current = callback;
    setShow(true);
  }, []);

  /**
   * check for cache validation then remove it if expired and return it.
   * @return 3 saved value in form of array
   * */
  const validateCaches = () => {
    return (
      [
        [LOCAL_STORAGE_SUBMIT_PRIVACY_POLICIES, submitPayloadTTL],
        [LOCAL_STORAGE_ALLOWED_PRIVACY_POLICY, privacyPolicyCacheTTL],
        // in form of [<ref object to save into>, <time to live in day>, <cache key>]
      ] as Array<[string, number]>
    ).map(([key, duration]) => {
      const value = parseJsonStringSafely(localStorage.getItem(key)) as {
        createdAt: string;
      };
      if (!value || checkCacheExpired(value.createdAt, duration)) {
        localStorage.removeItem(key);
        return undefined;
      }
      return value;
    }) as [LocalStorageSubmitPolicies, LocalStorageAllowedStatus];
  };

  /**
   * this effect only run once auth loaded.
   * 1. validate caches (return cached values if valid or remove otherwise).
   * 2. validate submit (submit to api if a submit payload saved which retrieved in previous step)
   * 3. if step 3 success (submitted or nothing to submit yet) then validate policies (decide using cache or fetch from api)
   * 4. app is not IBanking App
   * */
  useEffect(() => {
    const isIBankingApp = partner ? isBankPartner(partner) : false;
    if (!authLoading && flags.epic_ecom_483 && !isIBankingApp) {
      const isAuthed = authService.isLoggedIn();
      const [savedSubmitPolicies, savedAllowedStatus] = validateCaches();
      validateSubmitPolicies(isAuthed, savedSubmitPolicies)
        .then(() => validatePolicies(isAuthed, savedAllowedStatus))
        .catch(() => {}); // error handled
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, partner]);

  const showWarningModal = () => {
    showModal?.({
      title: t('Thông báo'),
      children: (
        <Body>
          {t(
            'Vui lòng xác nhận đồng ý với các điều khoản và điều kiện dịch vụ để tiếp tục',
          )}
        </Body>
      ),
      okText: t('Đóng'),
      cancelButtonProps: { style: { display: 'none' } },
      showCloseIcon: false,
      closable: false,
    });
  };

  const buildPrivacyPolicyList = useCallback(() => {
    return (
      <PrivacyPolicies
        policies={policies}
        setPolicies={setPolicies}
        trackingScreenName={''}
        isLoggedIn={isServerless() && authService.isLoggedIn()}
      />
    );
  }, [authService, policies]);

  const isByPassShowPoliciesForUnLoggerUser = (
    loginMethod: string,
    userPhone?: string,
  ): boolean => {
    const LSKey = `${LOCAL_STORAGE_UNLOGGED_USER_PRIVACY_POLICIES}_${
      currentLanguage || 'vi'
    }`;
    const privacyPolicies: LocalStorageUnloggedUserPrivacyPolicies | undefined =
      parseJsonStringSafely(localStorage.getItem(LSKey));

    setUserSelectedLoginMethod({
      loginMethod,
      userPhone,
    });

    if (privacyPolicies) {
      const {
        facebookUserSelectedPolicies,
        googleUserSelectedPolicies,
        phoneUserSelectedPolicies,
      } = privacyPolicies;

      const checkUserAlreadyByPassEnterPolicies = (
        savedPolicies: UserResponsePolicy[],
      ): boolean => {
        // Return false if user uncheck every required policy
        return !savedPolicies.find(
          ({ isApprovalRequired, isApproved }) =>
            isApprovalRequired && !isApproved,
        );
      };

      switch (loginMethod) {
        case LOGIN_METHOD.FACEBOOK:
          if (
            !!facebookUserSelectedPolicies &&
            checkUserAlreadyByPassEnterPolicies(facebookUserSelectedPolicies)
          ) {
            return true;
          }
          break;
        case LOGIN_METHOD.GOOGLE:
          if (
            !!googleUserSelectedPolicies &&
            checkUserAlreadyByPassEnterPolicies(googleUserSelectedPolicies)
          ) {
            return true;
          }
          break;
        case LOGIN_METHOD.PHONE:
          if (userPhone && phoneUserSelectedPolicies) {
            const userSelectedPolicy = phoneUserSelectedPolicies[userPhone];
            if (
              !!userSelectedPolicy &&
              checkUserAlreadyByPassEnterPolicies(userSelectedPolicy)
            ) {
              return true;
            }
          }
          break;
      }

      return false;
    }

    return false;
  };

  return (
    <PrivacyPolicyContext.Provider
      value={{
        loading,
        policies,
        shouldShowSelector,
        showSelector,
        submitPoliciesApproval,
        buildPrivacyPolicyList,
        isByPassShowPoliciesForUnLoggerUser,
      }}
    >
      {children}
      {isMobile ? (
        <BottomSheet
          show={show}
          title={t('Thông báo')}
          closeBottomSheet={isLoginPage ? () => setShow(false) : undefined}
        >
          <Shape
            width="100%"
            padding="s16"
            style={{
              marginBottom: '64px',
              width: '100%',
            }}
          >
            {buildPrivacyPolicyList()}
            <BottomActionBar
              style={{
                display: 'absolute',
                gap: '8px',
              }}
            >
              {!isLoginPage && (
                <Button
                  width="100%"
                  variant="secondary"
                  onClick={() => {
                    setShow(false);
                    setShouldShowSelector(false);
                    if (policies.some((policy) => policy.isApprovalRequired)) {
                      trackManually(
                        'logoutButton',
                        'privacyMethod',
                        'loginMethod',
                        JSON.stringify({ screenName: 'userPrivacyPopup' }),
                      );
                      onSignOut(
                        authService,
                        notificationService,
                        notificationAppId,
                        window.location.origin,
                        epicOmni1825,
                      );
                    } else {
                      trackManually(
                        'quitButton',
                        'privacyMethod',
                        window.location.href,
                        JSON.stringify({ screenName: 'userPrivacyPopup' }),
                      );
                    }
                  }}
                >
                  {policies.some((policy) => policy.isApprovalRequired)
                    ? t('Đăng xuất')
                    : t('Bỏ qua')}
                </Button>
              )}
              <Button
                width="100%"
                variant="primary"
                onClick={() => {
                  trackManually(
                    'confirmButton',
                    'privacyMethod',
                    window.location.href,
                    JSON.stringify({
                      screenName: 'userPrivacyPopup',
                    }),
                  );
                  submitPoliciesApproval(submitCallback.current);
                }}
              >
                {t('Xác nhận')}
              </Button>
            </BottomActionBar>
          </Shape>
        </BottomSheet>
      ) : (
        <Modal
          width="600px"
          visible={show}
          showCloseIcon={false}
          closable={false}
          okText={t('Xác nhận')}
          onOk={() => {
            trackManually(
              'confirmButton',
              'privacyMethod',
              window.location.href,
              JSON.stringify({
                screenName: 'userPrivacyPopup',
              }),
            );
            submitPoliciesApproval();
          }}
          cancelText={t('Đăng xuất')}
          onCancel={() => {
            setShow(false);
            trackManually(
              'logoutButton',
              'privacyMethod',
              'loginMethod',
              JSON.stringify({ screenName: 'userPrivacyPopup' }),
            );
            onSignOut(
              authService,
              notificationService,
              notificationAppId,
              window.location.origin,
              epicOmni1825,
            );
          }}
          title={t('Thông báo')}
        >
          <ModalBody style={{ padding: 0 }}>
            {buildPrivacyPolicyList()}
          </ModalBody>
        </Modal>
      )}
    </PrivacyPolicyContext.Provider>
  );
};

export const usePrivacyPolicy = () => {
  const context = useContext(PrivacyPolicyContext);
  return context;
};

const validateApproval = (policies: PrivacyPolicy[]) => {
  return !policies.some(
    (policy) => policy.isApprovalRequired && !policy.isApproved,
  );
};

const saveSubmitPolicies = (
  policies: SubmittedPrivacyPolicy[],
  customCreatedAt?: string,
) => {
  localStorage.setItem(
    LOCAL_STORAGE_SUBMIT_PRIVACY_POLICIES,
    JSON.stringify({
      policies: policies,
      isSubmitted: false,
      createdAt: customCreatedAt || new Date().toString(),
    } as LocalStorageSubmitPolicies),
  );
};

const checkCacheExpired = (date: string, duration: number) => {
  return !(
    (new Date().getTime() - new Date(date).getTime()) / (24 * 3600 * 1000) <=
    duration
  );
};

const checkUserAllowed = (policies: PrivacyPolicy[]) =>
  !policies.some((policy) => policy.isApprovalRequired);
